/** Client-only header chrome sticky behavior (DOM). Re-exports SSR helpers from `-shared`. */

export * from './header-chrome-sticky-shared';

import { applyHeaderChromeThemeToMount, type StorefrontHeaderChromeColorSettings } from './header-chrome-theme';
import { resolveHeaderChromeSticky } from './header-chrome-sticky-shared';

const SSR_HEADER_SELECTOR = '[data-ssr-header="true"]';
const CHROME_SELECTOR = '[data-storefront-header-chrome-row="true"]';
const CHROME_SPACER_SELECTOR = '[data-header-chrome-sticky-spacer="true"]';
const CHROME_FIXED_CLASS = 'storefront-header-chrome--fixed';

type MountChromeStickyState = {
  scroll: () => void;
  resize: () => void;
};

const mountChromeStickyStates = new WeakMap<HTMLElement, MountChromeStickyState>();

function clearPositionStyles(element: HTMLElement): void {
  element.style.removeProperty('position');
  element.style.removeProperty('top');
  element.style.removeProperty('left');
  element.style.removeProperty('right');
  element.style.removeProperty('width');
  element.style.removeProperty('z-index');
}

function findHeaderChrome(headerMount: HTMLElement): HTMLElement | null {
  const chrome = headerMount.querySelector(CHROME_SELECTOR);
  return chrome instanceof HTMLElement ? chrome : null;
}

function getOrCreateChromeSpacer(chrome: HTMLElement): HTMLElement {
  const header = chrome.closest(SSR_HEADER_SELECTOR);
  const parent = header instanceof HTMLElement ? header : chrome.parentElement;
  const existing = parent?.querySelector(CHROME_SPACER_SELECTOR);
  if (existing instanceof HTMLElement) {
    return existing;
  }
  const spacer = document.createElement('div');
  spacer.setAttribute('data-header-chrome-sticky-spacer', 'true');
  spacer.hidden = true;
  spacer.setAttribute('aria-hidden', 'true');
  chrome.insertAdjacentElement('afterend', spacer);
  return spacer;
}

function detachChromeScrollSticky(headerMount: HTMLElement): void {
  const state = mountChromeStickyStates.get(headerMount);
  if (!state) return;
  window.removeEventListener('scroll', state.scroll);
  window.removeEventListener('resize', state.resize);
  mountChromeStickyStates.delete(headerMount);
}

function releaseFixedChrome(chrome: HTMLElement, headerMount: HTMLElement): void {
  chrome.classList.remove(CHROME_FIXED_CLASS);
  clearPositionStyles(chrome);
  const spacer = headerMount.querySelector(CHROME_SPACER_SELECTOR);
  if (spacer instanceof HTMLElement) {
    spacer.hidden = true;
    spacer.style.height = '';
  }
  headerMount.style.removeProperty('--storefront-fixed-chrome-height');
  window.dispatchEvent(new CustomEvent('pagayo:header-chrome-sticky-change'));
}

export function isHeaderChromeStickyEnabled(headerMount: HTMLElement): boolean {
  if (headerMount.dataset.headerChromeSticky === 'true') {
    return true;
  }
  const header = headerMount.querySelector(SSR_HEADER_SELECTOR);
  if (header instanceof HTMLElement && header.dataset.headerChromeSticky === 'true') {
    return true;
  }
  const chrome = findHeaderChrome(headerMount);
  return chrome?.dataset.headerChromeSticky === 'true';
}

export function isHeaderChromeRowFixed(headerMount: HTMLElement): boolean {
  const chrome = findHeaderChrome(headerMount);
  return chrome instanceof HTMLElement && chrome.classList.contains(CHROME_FIXED_CLASS);
}

/** Measured chrome row height (for menu `top` when chrome sticky is on). */
export function getHeaderChromeRowHeight(headerMount: HTMLElement): number {
  const chrome = findHeaderChrome(headerMount);
  return chrome instanceof HTMLElement ? chrome.offsetHeight : 0;
}

/** Document offset where the chrome row becomes fixed. */
export function measureHeaderChromeOffsetTop(headerMount: HTMLElement): number | null {
  const chrome = findHeaderChrome(headerMount);
  if (!(chrome instanceof HTMLElement)) {
    return null;
  }
  return chrome.getBoundingClientRect().top + window.scrollY;
}

/** Height of fixed chrome row (0 when not fixed). Used to offset sticky menu bar. */
export function getFixedChromeHeight(headerMount: HTMLElement): number {
  if (!isHeaderChromeRowFixed(headerMount)) {
    return 0;
  }
  return getHeaderChromeRowHeight(headerMount);
}

/** Keeps menu `top: var(--storefront-fixed-chrome-height)` in sync before/during sticky. */
export function syncHeaderChromeHeightVar(headerMount: HTMLElement): void {
  const targets: HTMLElement[] = [headerMount];
  const header = headerMount.querySelector(SSR_HEADER_SELECTOR);
  if (header instanceof HTMLElement) {
    targets.push(header);
  }

  if (!isHeaderChromeStickyEnabled(headerMount)) {
    for (const el of targets) {
      el.style.removeProperty('--storefront-fixed-chrome-height');
    }
    return;
  }

  const height = getHeaderChromeRowHeight(headerMount);
  if (height > 0) {
    const value = `${height}px`;
    for (const el of targets) {
      el.style.setProperty('--storefront-fixed-chrome-height', value);
    }
  } else {
    for (const el of targets) {
      el.style.removeProperty('--storefront-fixed-chrome-height');
    }
  }
}

function attachChromeScrollSticky(headerMount: HTMLElement, chrome: HTMLElement): void {
  detachChromeScrollSticky(headerMount);
  const spacer = getOrCreateChromeSpacer(chrome);
  let chromeOffsetTop: number | null = null;

  const measureOffsetTop = (): number => chrome.getBoundingClientRect().top + window.scrollY;

  const update = (): void => {
    if (!chrome.isConnected || !headerMount.isConnected) {
      detachChromeScrollSticky(headerMount);
      return;
    }

    if (chromeOffsetTop === null) {
      chromeOffsetTop = measureOffsetTop();
    }

    const shouldFix = window.scrollY >= chromeOffsetTop - 0.5;

    if (shouldFix) {
      const height = chrome.offsetHeight;
      spacer.style.height = `${height}px`;
      spacer.hidden = false;
      chrome.classList.add(CHROME_FIXED_CLASS);
      syncHeaderChromeHeightVar(headerMount);
      window.dispatchEvent(new CustomEvent('pagayo:header-chrome-sticky-change'));
      return;
    }

    releaseFixedChrome(chrome, headerMount);
  };

  const onScroll = (): void => {
    update();
  };

  const onResize = (): void => {
    chromeOffsetTop = null;
    syncHeaderChromeHeightVar(headerMount);
    update();
  };

  mountChromeStickyStates.set(headerMount, { scroll: onScroll, resize: onResize });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  update();
}

export function applyHeaderChromeStickyToMount(
  headerMount: HTMLElement,
  chromeSticky: boolean,
  themeSettings: StorefrontHeaderChromeColorSettings = {},
): void {
  const sticky = resolveHeaderChromeSticky(chromeSticky);
  const chrome = findHeaderChrome(headerMount);

  applyHeaderChromeThemeToMount(headerMount, themeSettings);

  if (!(chrome instanceof HTMLElement)) {
    return;
  }

  chrome.dataset.headerChromeSticky = sticky ? 'true' : 'false';
  headerMount.dataset.headerChromeSticky = sticky ? 'true' : 'false';

  detachChromeScrollSticky(headerMount);

  if (!sticky) {
    releaseFixedChrome(chrome, headerMount);
    return;
  }

  syncHeaderChromeHeightVar(headerMount);
  attachChromeScrollSticky(headerMount, chrome);
}
